import { useRef, useState } from 'react'

import { EllipsisVertical, GripVertical, Plus } from 'lucide-react'
import { FormCard, FormSectionCollapsible, Row, Col, FormGroup, Label, Button, ButtonIcon, Checkbox, ColorPickerInputGroup, DefaultPopover, DefaultTooltip, Input, InputGroup, InputGroupText, List, ListItem, Switch, cn } from 'twyne-new.ui'

import { DndSortableList, useSortableDndListItem } from '@/components/SortableDnd'
import { sanitizeCustomFieldAnswer } from '@/utils'
import PageVariablesEditorFrame from '@Components/Mergetag/PageVariablesEditorFrame'
import CodeEditor from '@Components/form/CodeEditor'

import { templateAlias } from './formUtils'

const ANSWER_TYPES = {
  'tcpa-question': {
    settings: {
      multiple: ({ fieldType }) => fieldType !== 'boolean',
      required: ({ data }) => !!data.isMultipleAnswers,
      manualSubmit: ({ data }) => !data.isMultipleAnswers,
      skip: ({ data }) => !data.isMultipleAnswers,
    },
  },
  'tcpa-question-custom': {
    settings: {
      multiple: ({ fieldType, data }) => fieldType !== 'boolean' && data.templateType === 'only-buttons',
      required: ({ data }) => !!data.isMultipleAnswers || ['button-and-dropdown', 'only-dropdown'].includes(data.templateType),
      manualSubmit: ({ data }) => data.templateType === 'button-and-dropdown' || !data.isMultipleAnswers,
      skip: ({ data }) => !data.isMultipleAnswers,
    },
  },
  'tcpa-call-question': {
    extra: { toggle: 'call', field: 'phone', label: 'Click to call', inputType: 'number', placeholder: 'Click to call disabled' },
    settings: { skip: () => true },
  },
  'tcpa-linkout-question': {
    extra: { toggle: 'linkout', field: 'url', label: 'Linkout', inputType: 'url', placeholder: 'Linkout disabled' },
    settings: { skip: () => true },
  },
}

export const hasQuestionAnswers = type => !!ANSWER_TYPES[type]

const ADDABLE_FIELD_TYPES = ['integer', 'input', 'date']

const ADD_ANSWER_TIPS = {
  select: 'Answers come from the options of the selected custom field.',
  boolean: 'Answers are fixed as Yes and No for boolean custom fields.',
}

const addAnswerTip = fieldType => ADD_ANSWER_TIPS[fieldType] || 'Custom fields needs to be of type integer, input, or date to add answers.'

const DEFAULT_ANSWER = {
  key: '',
  answer: '',
  fontcolor: '255,255,255,1',
  color: '113,113,113,1',
}

const COLOR_FIELDS = ['color', 'fontcolor']

const keyInputType = fieldType => {
  if (fieldType === 'integer') return 'number'
  if (fieldType === 'date') return 'date'
  return 'text'
}

const parseColor = value => value?.rgbaParsed?.replace('rgba(', '').replace(')', '')

const toHex = value => {
  const [r, g, b] = `${value || ''}`.split(',').map(Number)
  if ([r, g, b].some(channel => !Number.isFinite(channel))) {
    return ''
  }
  return `#${[r, g, b].map(channel => channel.toString(16).padStart(2, '0')).join('')}`.toUpperCase()
}

const sanitize = (fieldType, field, value) => fieldType === 'input' && typeof value === 'string' && !COLOR_FIELDS.includes(field) ? sanitizeCustomFieldAnswer(value) : value

const stopPropagation = event => event.stopPropagation()

export default function QuestionAnswers({ type, integrationData, disabled, onChange, onPageChange, badge }) {
  const settings = ANSWER_TYPES[type]
  const [adding, setAdding] = useState(null)
  const [editing, setEditing] = useState(null)
  const uids = useRef({ map: new WeakMap(), next: 0 })

  if (!settings) {
    return null
  }

  const data = integrationData[templateAlias[type]] || {}
  const answers = data.answers || []
  const customField = (integrationData.extraFields || []).find(({ key }) => key === data.cfid) || {}
  const fieldType = customField.type
  const keysDisabled = ['select', 'boolean'].includes(fieldType)
  const canAdd = ADDABLE_FIELD_TYPES.includes(fieldType)

  const uidOf = answer => {
    const { map } = uids.current
    if (!map.has(answer)) {
      map.set(answer, `answer-${uids.current.next++}`)
    }
    return map.get(answer)
  }

  const rows = answers.map((answer, index) => ({ ...answer, index, uid: uidOf(answer) }))
  const skipRow = data.skipOption && data.skipAnswer ? { ...data.skipAnswer, index: 'skip', uid: 'skip' } : null

  const openEditor = (row, isSkip) => !disabled && setEditing({ uid: row.uid, index: row.index, isSkip, draft: { ...row } })

  const changeDraft = (field, value) => setEditing(current => ({
    ...current,
    draft: { ...current.draft, [field]: sanitize(fieldType, field, value) },
  }))

  const handleAdd = () => {
    onChange('answers', [...answers, {
      ...DEFAULT_ANSWER,
      key: sanitize(fieldType, 'key', adding.key),
      answer: sanitize(fieldType, 'answer', adding.answer),
    }])
    setAdding(null)
  }

  const handleSave = () => {
    const { index: _index, uid: _uid, ...answer } = editing.draft
    if (editing.isSkip) {
      onChange('skipAnswer', answer)
    } else {
      const next = [...answers]
      next[editing.index] = answer
      onChange('answers', next)
    }
    setEditing(null)
  }

  const handleDelete = () => {
    const next = [...answers]
    next.splice(editing.index, 1)
    onChange('answers', next)
    setEditing(null)
  }

  const handleSortEnd = sorted => onChange('answers', sorted.map(({ index }) => answers[index]))

  const editor = (row, isSkip) => <AnswerEditor
    open={editing?.uid === row.uid}
    onOpenChange={open => open ? openEditor(row, isSkip) : setEditing(null)}
    draft={editing?.uid === row.uid ? editing.draft : null}
    extra={settings.extra}
    keyType={keyInputType(fieldType)}
    keyDisabled={keysDisabled || isSkip || disabled}
    disabled={disabled}
    onChangeField={changeDraft}
    onSave={handleSave}
    onDelete={isSkip || keysDisabled ? null : handleDelete}
  />

  return <Col>
    <FormCard title={<span className='flex items-center gap-2'>
      Question &amp; answers
      {badge}
    </span>}>
      <Row>
        <Col size='12'>
          <FormGroup>
            <Label>Question</Label>
            <PageVariablesEditorFrame disabled={disabled}>
              <CodeEditor
                resizable
                height='100px'
                width='100%'
                name='question'
                value={data.question || ''}
                onChange={value => onChange('question', value)}
                disabled={disabled}
              />
            </PageVariablesEditorFrame>
          </FormGroup>
        </Col>
        <Col size='12'>
          <div className='flex justify-between items-center'>
            <Label>Answers</Label>
            <AddAnswerPopover
              canAdd={canAdd}
              disabled={disabled}
              tip={addAnswerTip(fieldType)}
              keyType={keyInputType(fieldType)}
              draft={adding}
              onOpenChange={open => setAdding(open ? { key: '', answer: '' } : null)}
              onChangeField={(field, value) => setAdding({ ...adding, [field]: sanitize(fieldType, field, value) })}
              onAdd={handleAdd}
            />
          </div>
          <DndSortableList items={rows} itemIds={rows.map(({ uid }) => uid)} onSortEnd={handleSortEnd} activationConstraint={{ distance: 5 }}>
            <List border className='max-h-[30vh] px-0!'>
              {rows.map(row => <SortableAnswerItem
                key={row.uid}
                disabled={disabled}
                row={row}
                onOpen={() => openEditor(row, false)}
              >
                {editor(row, false)}
              </SortableAnswerItem>)}
              {skipRow ? <AnswerItem row={skipRow} disabled={disabled} onOpen={() => openEditor(skipRow, true)}>
                {editor(skipRow, true)}
              </AnswerItem> : null}
              {!rows.length && !skipRow ? <span className='flex justify-center p-2 twy-text-neutral'>
                No answers to show.
              </span> : null}
            </List>
          </DndSortableList>
        </Col>
        {settings.settings ? <Col size='12'>
          <FormSectionCollapsible title='Stored values and styling' titleClass='text-base' triggerClass='flex-row-reverse justify-end gap-2'>
            <Row>
              <AnswerSettings
                rules={settings.settings}
                data={data}
                fieldType={fieldType}
                disabled={disabled}
                onChange={onChange}
                onPageChange={onPageChange}
              />
            </Row>
          </FormSectionCollapsible>
        </Col> : null}
      </Row>
    </FormCard>
  </Col>
}

function AnswerSettings({ rules, data, fieldType, disabled, onChange, onPageChange }) {
  const visible = Object.fromEntries(Object.entries(rules).map(([name, rule]) => [name, rule({ data, fieldType })]))

  const handleMultiple = checked => {
    onChange('manualSubmit', false)
    onChange('required', false)
    onChange('isMultipleAnswers', checked)
  }

  const handleManualSubmit = checked => {
    onChange('manualSubmit', checked)
    checked && onPageChange({ hideHostedSubmit: false })
  }

  const handleSkip = checked => {
    onChange('skipOption', checked)
    onChange('skipAnswer', checked ? { ...DEFAULT_ANSWER, key: '$skip', answer: 'Skip', disabled: true } : null)
  }

  return <>
    {visible.multiple ? <Toggle label='Multiple answers' checked={data.isMultipleAnswers} disabled={disabled} onChange={handleMultiple} /> : null}
    {visible.required ? <Toggle label='Required' checked={data.required} disabled={disabled} onChange={checked => onChange('required', checked)} /> : null}
    {visible.manualSubmit ? <Toggle label='Manual Submit' checked={data.manualSubmit} disabled={disabled} onChange={handleManualSubmit} /> : null}
    {visible.skip ? <Toggle label='Skip option' checked={data.skipOption} disabled={disabled} onChange={handleSkip} /> : null}
  </>
}

function Toggle({ label, checked, disabled, onChange }) {
  return <Col size='3'>
    <FormGroup>
      <Label>{label}</Label>
      <Switch checked={checked || false} disabled={disabled} onChange={event => onChange(event.target.checked)} />
    </FormGroup>
  </Col>
}

function AnswerItem({ row, onOpen, children, disabled, sortable }) {
  return <ListItem onClick={onOpen} {...sortable} className={cn(disabled && 'opacity-50 cursor-not-allowed', sortable?.className)}>
    <div className='flex items-center gap-2 w-full'>
      {sortable ? <GripVertical size={15} className='shrink-0' /> : <span className='w-[15px] shrink-0' />}
      <span className='truncate grow'>{row.answer}</span>
      {children}
    </div>
  </ListItem>
}

function SortableAnswerItem({ row, onOpen, children, disabled }) {
  const { setNodeRef, style, attributes, listeners } = useSortableDndListItem(row.uid, { disabled })
  const { opacity: _opacity, ...dimmable } = style
  return <AnswerItem
    row={row}
    onOpen={onOpen}
    disabled={disabled}
    sortable={{ ref: setNodeRef, style: disabled ? dimmable : style, className: disabled ? undefined : 'cursor-grab', ...attributes, ...listeners }}
  >
    {children}
  </AnswerItem>
}

function AnswerEditor({ open, onOpenChange, draft, extra, keyType, keyDisabled, disabled, onChangeField, onSave, onDelete }) {
  return <DefaultPopover
    trigger={<ButtonIcon content='Edit answer' disabled={disabled}>
      <EllipsisVertical size={16} />
    </ButtonIcon>}
    title='Edit answer'
    triggerProps={{ asChild: true, onClick: stopPropagation, onMouseDown: stopPropagation, onPointerDown: stopPropagation }}
    wrapperProps={{ open, onOpenChange }}
    contentProps={{ side: 'left', onClick: stopPropagation, onMouseDown: stopPropagation, onPointerDown: stopPropagation }}
  >
    {draft ? <div className='flex flex-col gap-4'>
      <FormGroup>
        <Label>Key</Label>
        <Input
          type={keyType}
          value={draft.key || ''}
          disabled={keyDisabled}
          placeholder='Key'
          onChange={event => onChangeField('key', event.target.value)}
        />
      </FormGroup>
      <FormGroup>
        <Label>Answer</Label>
        <Input
          value={draft.answer || ''}
          disabled={disabled}
          placeholder='Answer'
          onChange={event => onChangeField('answer', event.target.value)}
        />
      </FormGroup>
      {extra ? <FormGroup>
        <Label>{extra.label}</Label>
        <InputGroup>
          <InputGroupText addonType='append'>
            <Checkbox
              checked={draft[extra.toggle] || false}
              onChange={checked => onChangeField(extra.toggle, checked)}
              disabled={disabled}
            />
          </InputGroupText>
          <Input
            type={extra.inputType}
            value={draft[extra.field] || ''}
            disabled={!draft[extra.toggle] || disabled}
            placeholder={!draft[extra.toggle] ? extra.placeholder : null}
            onChange={event => onChangeField(extra.field, event.target.value)}
          />
        </InputGroup>
      </FormGroup> : null}
      <ColorField
        label='Background color'
        value={draft.color}
        disabled={disabled}
        onChange={value => onChangeField('color', value)}
      />
      <ColorField
        label='Font color'
        value={draft.fontcolor}
        disabled={disabled}
        onChange={value => onChangeField('fontcolor', value)}
      />
      <div className='flex gap-3 items-center'>
        <Button color='accent' className='flex-1 justify-center' onClick={onSave} disabled={disabled}>Apply</Button>
        {onDelete ? <Button variant='ghost' color='danger' className='flex-1 justify-center' onClick={onDelete} disabled={disabled}>
          Remove
        </Button> : null}
      </div>
    </div> : null}
  </DefaultPopover>
}

function AddAnswerPopover({ canAdd, disabled, tip, keyType, draft, onOpenChange, onChangeField, onAdd }) {
  const trigger = <Button variant='ghost' color='accent' size='sm' disabled={disabled || !canAdd}>
    <Plus size={14} />
    Add answer
  </Button>

  if (disabled || !canAdd) {
    return <DefaultTooltip content={!canAdd && tip}>
      <span>{trigger}</span>
    </DefaultTooltip>
  }

  return <DefaultPopover
    title='Add answer'
    trigger={trigger}
    triggerProps={{ asChild: true }}
    wrapperProps={{ open: !!draft, onOpenChange }}
  >
    {draft ? <div className='flex flex-col gap-4'>
      <FormGroup>
        <Label>Key</Label>
        <Input
          type={keyType}
          value={draft.key}
          placeholder='Key'
          onChange={event => onChangeField('key', event.target.value)}
        />
      </FormGroup>
      <FormGroup>
        <Label>Answer</Label>
        <Input
          value={draft.answer}
          placeholder='Answer'
          onChange={event => onChangeField('answer', event.target.value)}
        />
      </FormGroup>
      <div className='flex gap-3 items-center'>
        <Button color='accent' className='flex-1 justify-center' onClick={onAdd}>Add</Button>
      </div>
    </div> : null}
  </DefaultPopover>
}

function ColorField({ label, value, disabled, onChange }) {
  return <FormGroup>
    <Label>{label}</Label>
    <div className='flex gap-2 items-center'>
      <ColorPickerInputGroup
        components={['picker', 'preview']}
        value={value}
        onChange={color => onChange(parseColor(color))}
        disabled={disabled}
      />
      <Input value={toHex(value)} placeholder='#000000' disabled />
    </div>
  </FormGroup>
}
